import {
  DataGridDensity,
  GlobalPassthroughs,
  Readings,
  Series,
  TableDashletItem,
  TableDashletMultiSeries,
  TableDashletSingleSeries,
  TableDensity,
  TableSortState,
} from '../../../types';
import {
  AsyncSensorData,
  SensorDataRecords,
  SensorMeasureData,
} from '../../../core/types/data.types';
import { readingIndex } from '../../../core/utils/data.utils';
import { isSensorSeries, isSimulationSeries } from '../../../utils';
import * as Styled from './TableDashlet.styles';
import { useMemo } from 'react';
import DataGridDashlet from '../../atoms/DataGrid/DataGrid';
import { GridCellSelectionModel } from '@mui/x-data-grid-premium';
import { DataGridProps, Resolutions } from '@innovyze/stylovyze';
import * as SeriesData from '../../../_next/core/series-data';

export interface TableDashletProps extends GlobalPassthroughs {
  series: TableDashletSingleSeries | TableDashletMultiSeries;
  data?: SensorDataRecords;
  readings?: Readings[];
  loading?: boolean;
  page?: number;
  onPageChange?: (page: number) => void;
  sort?: TableSortState;
  onSortChange?: (sort?: TableSortState) => void;
  useMostRecentReading?: boolean;
  showUnits?: boolean;
  showDateTime?: boolean;
  density?: TableDensity;
  onSelectionChange?: (model: GridCellSelectionModel) => void;
  conditionalFormatting?: DataGridProps['conditionalFormatting'];
  columnConditionalFormatting?: DataGridProps['columnFormatting'];
  onColumnSelectedChange?: (column: string) => void;
  showSelection?: boolean;
  showColumnSelection?: boolean;
  showSensorAlias?: boolean;
  clearSelection?: boolean;
  simulationsStatus?: SeriesData.Status;
  uniqueRows?: boolean;
}

const checkIfHasMultipleSensors = (dataRecords?: SensorDataRecords) => {
  return Object.keys(dataRecords ?? {}).length > 1 ? true : false;
};

const checkIfDataIsLoading = (
  series: TableDashletSingleSeries | TableDashletMultiSeries,
  dataRecords: SensorDataRecords | undefined,
  simulationsStatus?: SeriesData.Status
) => {
  const seriesArray = Array.isArray(series) ? series : [series];

  return seriesArray.some((_series) => {
    const loadingStatus = ['idle', 'loading'];
    if (isSimulationSeries(_series)) {
      const data = dataRecords[_series.simulation_name]?.[Resolutions.RAW];
      return (
        !data ||
        loadingStatus.includes(data?.status ?? 'idle') ||
        loadingStatus.includes(simulationsStatus ?? 'idle')
      );
    }
    if (_series?.customData) return false;
    if (!dataRecords) return true;
    const asyncData = dataRecords[_series?.sensorId]?.[_series.resolution];
    return loadingStatus.includes(asyncData?.status ?? 'idle');
  });
};

const getReadingsValues = (
  measurements: SensorMeasureData[number],
  readings: Readings[]
) => {
  const values: Partial<Record<Readings, number>> = {};

  for (const reading of readings) {
    values[reading] = measurements[readingIndex[reading]];
  }

  return values;
};

const makeTableDashletItem = (
  timestamp: number,
  readings: Readings[],
  series: TableDashletSingleSeries,
  data: {
    measurements: SensorMeasureData;
    timestamps: number[];
    unit?: string | undefined;
  }
) => {
  return {
    sensorName: isSensorSeries(series)
      ? (series.alias ?? series.sensorId)
      : series.simulation_name,
    unit: data.unit ?? '',
    timestamp,
    ...getReadingsValues(data.measurements[timestamp], readings),
  };
};

/*
 * Create a unique key for each series and timestamp combination
 * to avoid duplicate entries in the table.
 */
const createSeriesHashKey = (
  series: Series | TableDashletSingleSeries,
  timestamp: number
) => {
  if (isSensorSeries(series)) `${series.sensorId}-${timestamp}`;
  else if (isSimulationSeries(series)) `${series.simulation_name}-${timestamp}`;
  return `unknown-${timestamp}`;
};
const makeTableDashletItems = (
  series: TableDashletSingleSeries | TableDashletMultiSeries,
  dataRecords: SensorDataRecords | undefined,
  readings: Readings[],
  isLoading: boolean,
  useMostRecentReading?: boolean,
  uniqueRows?: boolean
): TableDashletItem[] => {
  if (!dataRecords || isLoading) return [];

  const items: TableDashletItem[] = [];
  const seriesArray = Array.isArray(series) ? series : [series];

  const seenSeriesKeys = new Set<string>([]);
  for (const _series of seriesArray) {
    seenSeriesKeys.clear();
    // Handle sensor series with custom data
    if (isSensorSeries(_series) && _series.customData) {
      const timestamps = Object.keys(_series.customData);
      for (const timestamp of timestamps) {
        const seriesKey = createSeriesHashKey(_series, parseInt(timestamp));
        if (uniqueRows && seenSeriesKeys.has(seriesKey)) continue; // ? Skip duplicates
        items.push(
          makeTableDashletItem(parseInt(timestamp), readings, _series, {
            measurements: _series.customData,
            timestamps: [],
            unit: _series.customUnit,
          })
        );
        seenSeriesKeys.add(seriesKey);
      }
      continue;
    }
    let data: AsyncSensorData['data'];
    if (isSensorSeries(_series)) {
      data = dataRecords[_series.sensorId]?.[_series.resolution].data;
    }
    if (isSimulationSeries(_series)) {
      data = dataRecords[_series.simulation_name]?.[Resolutions.RAW].data;
    }

    if (data && data.timestamps.length > 0) {
      if (useMostRecentReading) {
        const timestamp = data.timestamps[data.timestamps.length - 1];
        const seriesKey = createSeriesHashKey(_series, timestamp);
        if (uniqueRows && seenSeriesKeys.has(seriesKey)) continue; // ? Skip duplicates
        items.push(makeTableDashletItem(timestamp, readings, _series, data));
        seenSeriesKeys.add(seriesKey);
      } else {
        for (const timestamp of data.timestamps) {
          const seriesKey = createSeriesHashKey(_series, timestamp);
          if (uniqueRows && seenSeriesKeys.has(seriesKey)) continue; // ? Skip duplicates
          items.push(makeTableDashletItem(timestamp, readings, _series, data));
          seenSeriesKeys.add(seriesKey);
        }
      }
    }
  }

  return items;
};

const TableDashlet = (props: TableDashletProps): JSX.Element => {
  const readings =
    props.readings?.length > 0 ? props.readings : [Readings.Close];
  const loading = checkIfDataIsLoading(
    props.series,
    props.data,
    props.simulationsStatus
  );
  const items = makeTableDashletItems(
    props.series,
    props.data,
    readings,
    loading,
    props.useMostRecentReading,
    props.uniqueRows
  );

  const density = useMemo(() => {
    switch (props.density) {
      case TableDensity.dense:
        return DataGridDensity.dense;
      case TableDensity.regular:
        return DataGridDensity.regular;
      default:
        DataGridDensity.regular;
    }
  }, [props.density]);

  return (
    <Styled.TableDashletWrapper>
      <Styled.TableContainer>
        <DataGridDashlet
          density={density ?? DataGridDensity.regular}
          items={items}
          readings={readings}
          page={props.page}
          sort={props.sort}
          dataCy={props.dataCy}
          showUnits={props.showUnits}
          showDateTime={props.showDateTime}
          onPageChange={props.onPageChange}
          onSortChange={props.onSortChange}
          hasMultipleSensors={checkIfHasMultipleSensors(props.data)}
          onSelectionChange={props.onSelectionChange}
          conditionalFormatting={props.conditionalFormatting}
          columnConditionalFormatting={props.columnConditionalFormatting}
          onColumnSelectedChange={props.onColumnSelectedChange}
          showSelection={props.showSelection}
          showColumnSelection={props.showColumnSelection}
          showSensorAlias={props.showSensorAlias}
          clearSelection={props.clearSelection}
          loading={loading}
        />
      </Styled.TableContainer>
    </Styled.TableDashletWrapper>
  );
};

export default TableDashlet;
